import { createAsyncThunk } from '@reduxjs/toolkit';
import SelfServeCreditService from 'services/selfServeCredit/selfServeCredit.service';
import { selfServeCreditBalanceByDealershipIdSelector } from './selectors';

const getErrorMessage = (error) =>
	error?.response?.data?.errors?.[0] ||
	error?.data?.errors?.[0] ||
	error?.error?.errors?.[0] ||
	error?.error?.message ||
	error?.message ||
	'Unable to load self-serve credit balance.';

const getSelfServeCreditBalancePayload = ({ data }) => {
	const summary = data || {};
	const {
		dealershipId: responseDealershipId,
		amountDollars,
		expiringCreditAmountDollars,
		expiringCreditExpirationDate,
	} = summary;

	if (
		responseDealershipId === undefined ||
		responseDealershipId === null ||
		typeof amountDollars !== 'number' ||
		typeof expiringCreditAmountDollars !== 'number' ||
		typeof expiringCreditExpirationDate !== 'string'
	) {
		throw new Error('Self-serve credit balance response has an unexpected shape.');
	}

	return {
		dealershipId: responseDealershipId,
		amountDollars,
		expiringCreditAmountDollars,
		expiringCreditExpirationDate,
		fetchedAt: Date.now(),
	};
};

export const fetchSelfServeCreditBalance = createAsyncThunk(
	'selfServeCreditBalances/fetchSelfServeCreditBalance',
	async (_, { rejectWithValue }) => {
		try {
			const { data } = await SelfServeCreditService.instance.getSummary();
			return getSelfServeCreditBalancePayload({ data });
		} catch (error) {
			return rejectWithValue(getErrorMessage(error));
		}
	},
	{
		condition: ({ dealershipId }, { getState }) => {
			const currentRecord = selfServeCreditBalanceByDealershipIdSelector(getState(), dealershipId);
			return !currentRecord?.isLoading;
		},
	},
);

export const applySelfServeCredit = createAsyncThunk(
	'selfServeCreditBalances/applySelfServeCredit',
	async ({ transactionId, amount }, { rejectWithValue }) => {
		try {
			const { data } = await SelfServeCreditService.instance.applyCredit({ transactionId, amount });
			return data;
		} catch (error) {
			return rejectWithValue(getErrorMessage(error));
		}
	},
);

export const reverseSelfServeCredit = createAsyncThunk(
	'selfServeCreditBalances/reverseSelfServeCredit',
	async ({ transactionId }, { rejectWithValue }) => {
		try {
			const { data } = await SelfServeCreditService.instance.reverseCredit({ transactionId });
			return data;
		} catch (error) {
			return rejectWithValue(getErrorMessage(error));
		}
	},
);
